<template>
  <modal :name="name" height="auto" width="560" :scrollable="true" :clickToClose="false" @before-open="buscarVinculados">
    <CloseModal :close="close" />
    <div class="modalbox">
      <div class="modalbox-content">
        <h5 class="tinto-modal__title">Gerenciar EAN</h5>

        <CCard v-if="contextFields && contextFields.length" class="tinto-context-card mb-3">
          <CCardHeader class="tinto-context-card__header">
            <fa-icon icon="info-circle" class="mr-2" />
            <strong>{{ contextTitle }}</strong>
          </CCardHeader>
          <CCardBody class="tinto-context-card__body">
            <div class="row no-gutters">
              <div v-for="(field, idx) in contextFields" :key="idx" :class="field.size || 'col-md-6'">
                <div class="tinto-info-item">
                  <label class="tinto-info-item__label">{{ field.label }}</label>
                  <div class="tinto-info-item__value">
                    <CBadge v-if="field.badge" :color="field.badge">{{ field.value }}</CBadge>
                    <span v-else>{{ field.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <div class="tinto-section">
          <div class="tinto-section__label">EAN atual</div>
          <div v-if="eanAtual" class="tinto-ean-atual">
            <span class="tinto-ean-atual__valor" :class="{ 'tinto-ean-atual__valor--marcado': removerVinculo }">{{ eanAtual }}</span>
            <button v-if="!removerVinculo" class="button button-error button-sm" @click="marcarDesvinculacao" :disabled="saving" title="Marcar EAN para desvincular">
              <fa-icon icon="unlink" /> Desvincular
            </button>
            <button v-else class="button button-sm" @click="desfazerDesvinculacao" :disabled="saving" title="Desfazer">
              Desfazer
            </button>
          </div>
          <div v-else class="tinto-table__muted tinto-ean-atual__vazio">Sem EAN vinculado</div>
          <div v-if="eanAtual && !vinculadosIndisponivel" class="tinto-ean-produtos">
            <div v-if="carregandoVinculados" class="tinto-ean-produtos__status">
              <fa-icon icon="spinner" spin /> Buscando produto do EAN...
            </div>
            <template v-else>
              <div v-for="p in produtosVinculados" :key="p.id" class="tinto-ean-produtos__item" :title="p.codBarra">
                <fa-icon icon="link" /> <strong>{{ p.id }}</strong> — {{ p.descComp }}
              </div>
              <div v-if="produtosVinculados.length === 0" class="tinto-ean-atual__aviso">
                <fa-icon icon="exclamation-triangle" /> Nenhum produto ativo encontrado com este EAN.
              </div>
              <div v-if="produtosVinculados.length > 1" class="tinto-ean-atual__aviso">
                <fa-icon icon="exclamation-triangle" /> Mais de um produto com o mesmo EAN — a venda resolve um deles de forma imprevisível.
              </div>
            </template>
          </div>
          <div v-if="removerVinculo" class="tinto-ean-atual__aviso">
            <fa-icon icon="exclamation-triangle" /> O EAN será removido ao salvar.
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="tinto-label">Buscar Produto</label>
          <div class="input-group">
            <input class="form-control" v-model="busca" placeholder="Digite nome, código ou EAN..." :disabled="searching" @keyup.enter="buscar" />
            <button class="button button-primary input-group-addon-right" :disabled="searching" @click="buscar">
              <fa-icon v-if="searching" icon="spinner" spin />
              <fa-icon v-else icon="search" />
            </button>
          </div>
        </div>

        <div v-if="searching" class="tinto-busca-status">
          <fa-icon icon="spinner" spin /> Buscando...
        </div>

        <div v-else-if="resultados.length > 0" class="tinto-table-wrap" style="max-height:200px;overflow-y:auto;margin-bottom:12px">
          <table class="tinto-table">
            <tbody>
              <tr v-for="p in resultados" :key="p.id" @click="selecionar(p)" style="cursor:pointer" :class="{'table-active': produtoSelecionado && produtoSelecionado.id === p.id}">
                <td style="width:80px">{{ p.id }}</td>
                <td>{{ p.descComp }}</td>
                <td style="width:120px"><span v-if="p.codBarra">{{ p.codBarra }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="buscaRealizada" class="tinto-busca-vazio">
          <fa-icon icon="search" /> Produto não encontrado
        </div>

        <div v-if="produtoSelecionado" class="tinto-section">
          <span class="tinto-section__label">Produto selecionado</span>
          <div style="font-size:13px;color:#344054">{{ produtoSelecionado.descComp }}</div>
          <div style="font-size:11px;color:#667085">ID: {{ produtoSelecionado.id }}</div>
        </div>
      </div>
    </div>
    <div class="tinto-modal__footer">
      <button class="button" @click="close">Cancelar</button>
      <button class="button button-primary" @click="submit" :disabled="!temAlteracao || saving">Salvar</button>
    </div>
  </modal>
</template>

<script>
import CloseModal from "../../components/CloseModal";
import { get, put } from '@/helpers/apiRequest';
import { filtroBuscaProduto } from '@/helpers/filtroBuscaProduto';

export default {
  name: "TintometricoVincularEanModal",
  components: { CloseModal },

  props: {
    name:          { type: String, required: true },
    contextTitle:  { type: String, default: '' },
    contextFields: { type: Array,  default: () => [] },
    eanAtual:      { type: String, default: '' },
    endpoint:      { type: String, default: '' },
  },

  data() {
    return {
      busca: '',
      resultados: [],
      produtoSelecionado: null,
      removerVinculo: false,
      saving: false,
      searching: false,
      buscaRealizada: false,
      produtosVinculados: [],
      carregandoVinculados: false,
      vinculadosIndisponivel: false,
    };
  },

  computed: {
    temAlteracao() {
      return !!this.produtoSelecionado || this.removerVinculo;
    },
  },

  watch: {
    // O EAN é só um número na tela: resolve qual(is) produto(s) do ERP o
    // carregam. Mais de um resultado é possível (codbarraunitario não tem
    // unique) e zero resultado significa vínculo quebrado — a base/corante
    // some da venda sem erro em lugar nenhum.
    // O @before-open da modal (abaixo) cobre a reabertura com o mesmo EAN,
    // que este watch não pega (o Vue não dispara handler para valor igual).
    // Fica com immediate para cobrir a troca de linha com a modal já aberta.
    eanAtual: {
      immediate: true,
      handler() { this.buscarVinculados(); },
    },
  },

  methods: {
    reset() {
      this.busca = '';
      this.resultados = [];
      this.produtoSelecionado = null;
      this.removerVinculo = false;
      this.searching = false;
      this.buscaRealizada = false;
    },

    close() {
      this.reset();
      this.$modal.hide(this.name);
    },

    buscarVinculados() {
      this.produtosVinculados = [];
      this.vinculadosIndisponivel = false;
      this.carregandoVinculados = false;
      if (!this.eanAtual) return;
      // A modal é uma instância só por listagem: abrir outro registro dispara
      // outra busca com a anterior ainda em voo. A resposta atrasada de um EAN
      // antigo não pode sobrescrever a do EAN atual.
      const eanRequisitado = this.eanAtual;
      this.carregandoVinculados = true;
      get(`/admin/produtos?filtros[ean]=${encodeURIComponent(eanRequisitado)}&page=1`)
        .then(json => {
          if (this.eanAtual !== eanRequisitado) return;
          const itens = json.itens || [];
          // API sem filtros[ean] ignora o parâmetro e devolve a primeira página
          // inteira do catálogo: confere o código de barras no cliente. Página
          // cheia sem nenhum codBarra igual = backend desatualizado — esconde o
          // bloco em vez de afirmar vínculo (ou quebra de vínculo) que não existe.
          this.produtosVinculados = itens.filter(p => p.codBarra === eanRequisitado);
          this.vinculadosIndisponivel = itens.length > 0 && this.produtosVinculados.length === 0;
        })
        .catch(() => {
          if (this.eanAtual !== eanRequisitado) return;
          // Falha na consulta não é vínculo quebrado: esconde o bloco em vez
          // de exibir "nenhum produto" para um EAN que pode estar correto.
          this.vinculadosIndisponivel = true;
        })
        .finally(() => {
          if (this.eanAtual !== eanRequisitado) return;
          this.carregandoVinculados = false;
        });
    },

    buscar() {
      if (!this.busca || this.searching) return;
      const termo = this.busca.trim();
      this.searching = true;
      this.resultados = [];

      const consultar = filtro =>
        get(`/admin/produtos?${filtro}&page=1`).then(json => json.itens || []);

      // Regra de decisão (numérico longo = EAN, numérico curto = código do
      // produto, resto = descrição) centralizada em filtroBuscaProduto —
      // ver o helper para o porquê e para as outras cópias desta régua que
      // ainda ficaram de fora desta PR.
      const { filtro, conferirCodBarra } = filtroBuscaProduto(termo);
      let promessa = consultar(filtro);
      if (conferirCodBarra) {
        promessa = promessa.then(itens => {
          const porEan = itens.filter(p => p.codBarra === termo);
          return porEan.length ? porEan : consultar(`filtros[produto_id]=${termo}`);
        });
      }

      promessa
        .then(itens => { this.resultados = itens; })
        .catch(() => { this.resultados = []; })
        .finally(() => {
          this.searching = false;
          this.buscaRealizada = true;
        });
    },

    selecionar(produto) {
      this.produtoSelecionado = produto;
      this.removerVinculo = false;
    },

    marcarDesvinculacao() {
      this.removerVinculo = true;
      this.produtoSelecionado = null;
    },

    desfazerDesvinculacao() {
      this.removerVinculo = false;
    },

    submit() {
      if (!this.temAlteracao) return;
      const produtoId = this.removerVinculo ? null : this.produtoSelecionado.id;
      this.saving = true;
      put(this.endpoint, { produto_id: produtoId })
        .then(() => {
          this.$emit('saved');
          this.close();
        })
        .catch(e => { this.$swal({ title: 'Erro', text: e.message, icon: 'error' }); })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>
